import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { Visits } from '@types-lib';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';
import { Observable } from 'rxjs';

export class VisitOrdersSearchEndpoint {
  private env = inject<PIMSEnvironment>(ENVIRONMENT);
  private http = inject(HttpClient);

  // The search method
  get(pagination: Visits.OrderQuery): Observable<any> {
    const uiOnlyKeys = ['total', 'current', 'saveOnChange', 'saveFilter'];
    const keysToRemove = [
      ['partner', 'all'],
      ['is_slv', 'all'],
      ['state', 'All states'],
      ['category_code', 'All'],
      ['admin_status', 'Pending Review'],
    ];
    const queryObject = this.cleanQuery(pagination, keysToRemove, uiOnlyKeys);
    const query = this.createQueryObject(queryObject);

    // Create HttpParams object to handle query parameters
    let params = new HttpParams();
    (Object.keys(query) as Array<keyof Visits.OrderQuery>).forEach(key => {
      if (query[key] !== undefined) {
        params = params.set(key as string, query[key] as keyof Visits.OrderQuery);
      }
    });

    // Return the HTTP GET request as an observable
    return this.http.get(`${this.env.constants.ADMIN_API_URL}/admin/orders`, {
      params,
      headers: { 'Loading-Text': 'Loading Visits' },
    });
  }

  private createQueryObject(params: Visits.OrderQuery, polling = false) {
    // Default values
    const defaultParams = {
      limit: 100,
      offset: 0,
      sort: 'default',
      order: 'desc',
      timezone: 'Europe/London',
      is_batchable: false,
      polling,
    };

    // Merge defaultParams with params, using the values in `params` if they are provided
    const queryParams = Object.fromEntries(
      Object.entries({ ...defaultParams, ...params }).filter(
        ([key, value]) => (value !== undefined && value !== null && value !== '') || key === 'end_time',
      ),
    );
    const queryObject: Record<string, string | number | boolean | Date> = {};

    Object.keys(queryParams).forEach(key => {
      const value = queryParams[key];

      if (Array.isArray(value)) {
        // If the value is an array, join its elements with '|'
        queryObject[key as keyof Visits.OrderQuery] = value.join('|');
      } else if (key === 'end_time') {
        // Set `end_time` to a default of today's date if no value is provided
        queryObject[key] = value ? new Date(value as number).toISOString() : new Date().toISOString(); // Default to current date
      } else if (key === 'start_time' && value !== undefined) {
        // Convert `start_time` to ISO format
        queryObject[key] = new Date(value as number).toISOString();
      } else if (['limit', 'offset', 'total'].includes(key)) {
        // Convert the values into number or change it to 0 if its limit, offset or total
        queryObject[key] = Number(value) || 0;
      } else if (key === 'is_batchable') {
        // Check if its is_batchable and assign the value can do "value as boolean" but this is probably safer
        queryObject[key] = value ? value : false;
      } else if (value) {
        // Otherwise, add the key-value pair as-is if it's defined
        queryObject[key] = value;
      }
    });

    return queryObject;
  }

  private cleanQuery(query: Visits.OrderQuery, keysToRemove: string[][] = [], uiOnlyKeys: string[] = []) {
    // Remove keys based on specific conditions
    const queryCopy = { ...query };
    keysToRemove.forEach(([key, valueToRemove]) => {
      if (queryCopy[key as keyof Visits.OrderQuery] === valueToRemove) {
        delete queryCopy[key as keyof Visits.OrderQuery];
      }
    });

    // Remove UI-only keys unconditionally
    uiOnlyKeys.forEach(key => {
      if (key in queryCopy) {
        delete queryCopy[key as keyof Visits.OrderQuery];
      }
    });

    return queryCopy;
  }
}
